import React, { useEffect, useState } from 'react';
import { Button, Input, message, Spin, Tooltip, Typography } from 'antd';
import { QuoteLineItemProps } from '@partsbadger/types';
import { FileInfoPill, PartActions } from '../';
import * as api from '@partsbadger/utils';
import { PartPreview } from '@/components/Quotes';
import { ThreeDViewerModal, viewerPartInfoFromQuotePart } from '@/components/ThreeDViewerModal';
import { CloseOutlined, LoadingOutlined, SaveOutlined } from '@ant-design/icons';
import QuoteStore from '@/stores/QuoteStore';
import {
    buildManufacturingTypePayloadForStlPart,
    STL_MANUFACTURING_TYPE,
} from '@/utils/stlManufacturing';
import { isStlFile } from '@partsbadger/utils';
import DuplicationReasonPopup from '../../DuplicationReasonPopup';

interface IPartInfoProps {
    quoteToken?: string;
    part: QuoteLineItemProps;
    togglePartExpansion: (id: QuoteLineItemProps['id']) => void;
    onDuplicate: (id: QuoteLineItemProps['id']) => void;
    onRemove: (isVariation: boolean) => void;
    onNameChange?: (id: QuoteLineItemProps['id'], name: string) => void;
    loading?: boolean;
    isVariation: boolean;
    variantName?: string | null;
    isExpanded: boolean;
    isHobbyist?: boolean;
    onChange: (value: Record<string, unknown>) => void;
}

export const PartInfoCollapsed: React.FC<IPartInfoProps> = ({
    part,
    togglePartExpansion,
    onDuplicate,
    onRemove,
    onNameChange,
    loading = false,
    isVariation,
    variantName,
    isExpanded,
    onChange,
    quoteToken,
    isHobbyist,
}) => {
    const [partName, setPartName] = useState(part.name);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [urlFile3D, setUrlFile3D] = useState<string | null>(null);
    const [showFileUpdateModal, setShowFileUpdateModal] = useState(false);
    const [fileUpdateLoading, setFileUpdateLoading] = useState(false);
    /** Prefer static JSON URL (same as quote detail / parts library); signed URL only when `file_3djs` is missing. */
    const file3dJsUrl = part.product?.attachments?.file3d?.file_3djs ?? null;
    const [signedModelUrl, setSignedModelUrl] = useState<string | null>(null);
    const viewerModelUrl = file3dJsUrl ?? signedModelUrl;

    const ensureSignedModelUrl = async () => {
        if (file3dJsUrl) return;
        try {
            const data = await api.get(`/customer/quote/${quoteToken}/threed-signed-url/${part.uid}`);
            setSignedModelUrl(data.url);
        } catch (error) {
            console.log(error);
        }
    };

    const file3D = part.product?.attachments?.file3d?.filename
        ? {
              name: part.product.attachments.file3d.filename,
              url: part.product.attachments.file3d.filename,
          }
        : null;

    const file2DUrl = part.product?.attachments?.files_2d?.file ?? null;
    const file2D =
        part.product?.attachments?.files_2d && file2DUrl
            ? {
                  name: part.product.attachments.files_2d.filename,
                  url: file2DUrl,
              }
            : null;

    const { analysis_status } = part.product;

    const analyzer_3d_finished = analysis_status.file_3d.completed;
    const analyzer3dFailure = analysis_status?.file_3d?.status === 'FAILURE';

    const has3DFileAnalysisError = analyzer3dFailure && analyzer_3d_finished;
    const canEditPartName = !isVariation && part?.master_product_details?.editable !== false;
    const partNameTooltip = isVariation
        ? variantName
        : canEditPartName
        ? 'Rename part name'
        : 'Part name cannot be changed for this locked product';

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setPartName(inputValue);
    };

    const handleInputBlur = (e: React.MouseEvent) => {
        e.stopPropagation();

        if (!canEditPartName) {
            setPartName(part.name);
            setError(null);
            setIsEditing(false);
            return;
        }

        const trimmed = partName.trim();

        if (!trimmed) {
            setError('Part name is required');
            message.error('Part name is required');
            return;
        }
        setIsEditing(false);
        setError(null);
        onChange({ product: { name: partName } });
    };

    const handleSave = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        // Reuse existing validation + submit logic
        handleInputBlur((e as unknown) as React.MouseEvent);
    };

    const handleCancelEdit = () => {
        setPartName(part.name);
        setError(null);
        setIsEditing(false);
    };

    const showRequestFileUpdate =
        !isVariation && part?.master_product_details?.editable === false && part?.master_product_details?.id;

    const handleFileUpdateRequest = async (reason: string) => {
        if (!part?.master_product_details?.id) return;

        setFileUpdateLoading(true);
        try {
            await QuoteStore.duplicateMasterProduct(part.master_product_details.id, reason);
            setShowFileUpdateModal(false);
        } catch (error) {
            message.error('Error, please try again');
        } finally {
            setFileUpdateLoading(false);
        }
    };

    return (
        <div className="w-full mb-3">
            <DuplicationReasonPopup
                isOpen={showFileUpdateModal}
                partName={part.name}
                onConfirm={handleFileUpdateRequest}
                onCancel={() => setShowFileUpdateModal(false)}
                confirmLoading={fileUpdateLoading}
            />
            <div className="grid grid-cols-[auto,1fr] md:grid-cols-[auto,1fr,auto] items-start gap-2 w-full">
                {/* Thumbnail */}
                <div
                    className="row-start-1 col-start-1 flex-shrink-0"
                    onClick={() => {
                        togglePartExpansion(part.id);
                    }}
                >
                    {!isVariation &&
                        (part?.product?.thumbnail ? (
                            <div className="rounded-lg overflow-hidden border-2 border-slate-300 bg-slate-100">
                                {part.product.has_file_3d ? (
                                    <div
                                        onClick={() => {
                                            ensureSignedModelUrl();
                                        }}
                                    >
                                        <ThreeDViewerModal
                                            url={viewerModelUrl}
                                            partInfo={viewerPartInfoFromQuotePart(part)}
                                        >
                                            <PartPreview
                                                thumbnail={part.product?.thumbnail}
                                                has_file_3d={part.product.has_file_3d}
                                                width="85px"
                                                maxHeight="48px"
                                            />
                                        </ThreeDViewerModal>
                                    </div>
                                ) : part.product.has_file_2d && file2DUrl ? (
                                    <div
                                        onClick={event => {
                                            event.stopPropagation();
                                            window.open(file2DUrl, '_blank', 'width=800,height=800,left=200,top=200');
                                        }}
                                    >
                                        <PartPreview
                                            thumbnail={part.product?.thumbnail}
                                            has_file_3d={part.product.has_file_3d}
                                            width="85px"
                                            maxHeight="48px"
                                        />
                                    </div>
                                ) : (
                                    <PartPreview
                                        thumbnail={part.product?.thumbnail}
                                        has_file_3d={part.product.has_file_3d}
                                        width="85px"
                                        maxHeight="48px"
                                    />
                                )}
                            </div>
                        ) : null)}
                </div>

                {/* Actions (always top-right) */}
                <div className="row-start-1 col-start-2 md:col-start-3 justify-self-end flex-shrink-0">
                    <PartActions
                        isVariation={isVariation}
                        loading={loading}
                        onDuplicate={() => onDuplicate(part.product.master_product)}
                        onRemove={() => onRemove(isVariation)}
                        togglePartExpansion={() => togglePartExpansion(part.id)}
                        isExpanded={isExpanded}
                    />
                </div>

                {/* Name & File Info (right of thumbnail on sm+, full-width below on mobile) */}
                <div
                    className="row-start-2 col-span-2 md:row-start-1 md:col-span-1 md:col-start-2 w-full min-w-0"
                    onClick={() => {
                        togglePartExpansion(part.id);
                    }}
                >
                    <div className="flex flex-col md:flex-row items-start sm:items-center gap-1 sm:gap-2 mb-1 min-w-0 w-full">
                        <div>
                            <Tooltip title={<span className="text-xs">{partNameTooltip}</span>} placement="topLeft">
                                <div
                                    className={`min-w-0 max-w-full w-full ${
                                        canEditPartName ? 'cursor-pointer' : 'cursor-default'
                                    }`}
                                    aria-label="Part name"
                                    onClick={() => {
                                        if (canEditPartName) setIsEditing(true);
                                    }}
                                >
                                    {isEditing && canEditPartName ? (
                                        <>
                                            <div className="flex flex-col xl:flex-row md:items-center gap-2 w-full max-w-full">
                                                <div className="flex-1 min-w-0">
                                                    <Input
                                                        value={partName}
                                                        onChange={handleInputChange}
                                                        maxLength={250}
                                                        placeholder="Enter part name"
                                                        showCount
                                                        autoFocus
                                                        status={error ? 'error' : ''}
                                                        className="w-full text-base font-bold text-slate-900"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            setIsEditing(true);
                                                        }}
                                                        onPressEnter={e => handleSave(e)}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Escape') {
                                                                e.stopPropagation();
                                                                handleCancelEdit();
                                                            }
                                                        }}
                                                        disabled={loading}
                                                    />
                                                </div>

                                                <div className="flex items-center justify-end gap-2 flex-shrink-0">
                                                    <Button
                                                        size="small"
                                                        type="default"
                                                        icon={
                                                            <CloseOutlined className="flex align-middle text-center" />
                                                        }
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            handleCancelEdit();
                                                        }}
                                                        disabled={loading}
                                                    >
                                                        Cancel
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        type="primary"
                                                        icon={
                                                            <SaveOutlined className="flex align-middle text-center" />
                                                        }
                                                        onClick={handleSave}
                                                        loading={loading}
                                                        disabled={loading}
                                                    >
                                                        Save
                                                    </Button>
                                                </div>
                                            </div>
                                            {error && <span className="text-xs text-red-500">{error}</span>}
                                        </>
                                    ) : (
                                        <Spin
                                            size="small"
                                            spinning={loading}
                                            indicator={<LoadingOutlined />}
                                            className="part-name-spin"
                                        >
                                            <div className="part-name-wrapper">
                                                <span
                                                    className={`part-name ${
                                                        isVariation ? 'part-name--variation' : 'part-name--default'
                                                    }`}
                                                >
                                                    {isVariation ? variantName : part.name}
                                                </span>
                                            </div>
                                        </Spin>
                                    )}
                                </div>
                            </Tooltip>
                        </div>
                        <div className="sm:flex-shrink-0 sm:ml-1">
                            {part?.product?.dimensions_dict?.length &&
                            part?.product?.dimensions_dict?.width &&
                            part?.product?.dimensions_dict?.height ? (
                                <span className="text-xs text-slate-500">
                                    Dimensions:{' '}
                                    <Typography.Text type="secondary">{part?.product?.dimensions}</Typography.Text>
                                </span>
                            ) : null}
                        </div>
                    </div>

                    {!isVariation && (
                        <div className="min-w-0 mt-[11px] sm:mt-0">
                            <FileInfoPill
                                stepFile={file3D}
                                drawingFile={file2D}
                                dimensions={part.product?.dimensions}
                                onFileUpload={(fileType, fileId, fileName) => {
                                    if (fileType === 'file_3d' && fileName && isStlFile(fileName)) {
                                        const stlPayload = buildManufacturingTypePayloadForStlPart(
                                            part,
                                            STL_MANUFACTURING_TYPE
                                        );
                                        const stlProductPayload = stlPayload.product as
                                            | Record<string, unknown>
                                            | undefined;

                                        onChange({
                                            ...stlPayload,
                                            product: {
                                                ...stlProductPayload,
                                                file_3d: fileId,
                                            },
                                        });
                                        return;
                                    }

                                    onChange({
                                        product: { [fileType]: fileId },
                                    });
                                }}
                                manufacturingType={part.manufacturing_type}
                                has3DFileAnalysisError={has3DFileAnalysisError}
                                isHobbyist={isHobbyist}
                                onRemoveField={field => {
                                    onChange({
                                        product: { [field]: null },
                                    });
                                }}
                                lockFiles={!!showRequestFileUpdate}
                                lockedFileUpdateAction={
                                    showRequestFileUpdate ? (
                                        <div
                                            className="flex items-center gap-2 text-xs text-slate-500"
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <Tooltip title="Creates an editable copy of this part so you can upload new files. Pricing will be re-reviewed.">
                                                <Button
                                                    type="link"
                                                    size="small"
                                                    className="px-0"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        setShowFileUpdateModal(true);
                                                    }}
                                                >
                                                    Request file update
                                                </Button>
                                            </Tooltip>
                                        </div>
                                    ) : null
                                }
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PartInfoCollapsed;
